import { differenceInDays } from 'date-fns/differenceInDays';
import { useTranslations } from 'next-intl';

import { UserTierStatus } from '@flipster/apis/v1/user/types';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import UserVipstatusBasicLogoColorIcon from '@flipster/react/icon/generated/UserVipstatusBasicLogoColorIcon';
import UserVipstatusVipLogoColorIcon from '@flipster/react/icon/generated/UserVipstatusVipLogoColorIcon';
import { css, sva } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { convertDate } from '@flipster/utils/datetime';
import { isNullable } from '@flipster/utils/nullable';

const userTierBadgeRecipe = sva({
  slots: ['container', 'label'],
  base: {
    container: {
      display: 'inline-flex',
      alignItems: 'center',
      bg: 'background.default.level-4',
      rounded: 99,
      color: 'inverse.white',
    },
  },
  defaultVariants: {
    size: 'large',
  },
  variants: {
    size: {
      small: {
        container: { h: 20, gap: 2, pl: 2, pr: 6 },
        label: { textStyle: 'Text/XS/Regular' },
      },
      medium: {
        container: { h: 28, gap: 2, pl: 2, pr: 8 },
        label: { textStyle: 'Text/M/Strong' },
      },
      large: {
        container: { h: 36, gap: 4, pl: 4, pr: 10 },
        label: { textStyle: 'Title/XS' },
      },
    },
  },
});

const iconSizeMapper = {
  small: 16,
  medium: 24,
  large: 28,
};

const checkBasicUser = (status: UserTierStatus, tier?: number) => {
  if (isNullable(tier)) return status === 'TIER_STATUS_BASIC';
  return tier === 0;
};

interface UserTierIconProps {
  size?: number;
  status?: UserTierStatus;
  tier?: number;
}

export const UserTierIcon = ({ size = 20, status = 'TIER_STATUS_BASIC', tier }: UserTierIconProps) => {
  const Svg = checkBasicUser(status, tier) ? UserVipstatusBasicLogoColorIcon : UserVipstatusVipLogoColorIcon;

  return <Svg size={size} />;
};

interface UserTierBadgeProps {
  size?: 'small' | 'medium' | 'large';
  status?: UserTierStatus;
  tier?: number;
  boostAction?: {
    validUntil?: string;
    landingUrl?: string;
  };
}

/**
 * @param size small: h 20, medium: h 28, large: h 36
 * 디자이너와 완전히 합의된 컴포넌트가 아니라서 사용시 불편할수 있음
 * context: https://prextech.slack.com/archives/C088GNC6QRL/p1749795224378859?thread_ts=1749788674.040089&cid=C088GNC6QRL
 * @param status UserTierStatus
 * @param tier basic user, tier will be 0
 */

const getDateString = (date: Date) => {
  const now = new Date();
  const diff = differenceInDays(date, now);

  if (diff < 0) return null;
  if (diff === 0) return 'Last day';
  return `${diff + 1} days left`;
};

export const UserTierBadge = ({
  size = 'medium',
  status = 'TIER_STATUS_BASIC',
  tier = undefined,
  boostAction,
}: UserTierBadgeProps) => {
  const t = useTranslations();
  const isBasic = checkBasicUser(status, tier);
  const classes = userTierBadgeRecipe({ size });
  const boostValidUntilDate = convertDate(boostAction?.validUntil);
  const leftDaysString = boostValidUntilDate ? getDateString(boostValidUntilDate) : null;

  const content = (
    <>
      <UserTierIcon size={iconSizeMapper[size]} status={status} tier={tier} />
      <span className={classes.label}>
        {isBasic
          ? t('Autogen_common.r251126.s_Basic')
          : isNullable(tier)
            ? 'VIP'
            : t('Autogen_common.r251126.s_VIP_tier', {
                tier: tier,
              })}
      </span>
    </>
  );

  if (leftDaysString && boostAction?.landingUrl != null) {
    return (
      <IsomorphicLink href={boostAction.landingUrl} external className={classes.container}>
        {content}
        {leftDaysString && (
          <div className={flex({ ml: 6, align: 'center', gap: 4 })}>
            <span className={css({ textStyle: 'Text/S/Monospace/Strong', color: 'content.accent.VIP' })}>
              {leftDaysString}
            </span>
            <span className={flex({ color: 'inverse.white', align: 'center' })}>
              <ChevronRightIcon size={12} />
            </span>
          </div>
        )}
      </IsomorphicLink>
    );
  }

  return <div className={classes.container}>{content}</div>;
};
